import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import MembershipTypeForm from 'src/features/membershipType/components/forms/MembershipTypeForm';
import { membershipTypeFormSchema, MembershipTypeFormSchemaValues } from 'src/features/membershipType/formSchema';
import { MembershipType } from 'src/features/membershipType/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useParking } from 'src/providers/ParkingProvider';

interface MembershipTypeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedMembershipTemplate?: MembershipType;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: MembershipTypeFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function MembershipTypeFormModal<TData>({
    selectedMembershipTemplate,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: MembershipTypeFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    const { data: membershipTypeData } = useGetMembershipType({
        parkingId,
        membershipTypeId: selectedMembershipTemplate?.id ?? '',
        config: { enabled: !!selectedMembershipTemplate?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            translations: membershipTypeData?.translations ?? [],
            category: membershipTypeData?.category ?? undefined,
            site: membershipTypeData?.site?.id ?? undefined,
            has_pricing_plan: membershipTypeData?.has_pricing_plan ?? false,
            has_time_restriction: membershipTypeData?.has_time_restriction ?? false,
            pricing_plan_identity: membershipTypeData?.pricing_plan_identity ?? undefined,
            apply_pricing_per_space: membershipTypeData?.apply_pricing_per_space ?? false,
            allow_custom_space_count_definition: membershipTypeData?.allow_custom_space_count_definition ?? false,
            limit_duration: membershipTypeData?.limit_duration ?? false,
            duration_unit: membershipTypeData?.duration_unit ?? undefined,
            duration: membershipTypeData?.duration ?? undefined,
            max_allowed_spaces_count: membershipTypeData?.max_allowed_spaces_count ?? undefined,
            allow_access_over_capacity_limit: membershipTypeData?.allow_access_over_capacity_limit ?? false,
            contribute_to_available_spaces: membershipTypeData?.contribute_to_available_spaces ?? false,
            allow_extra_visitors: membershipTypeData?.allow_extra_visitors ?? false,
            max_extra_visitors_count: membershipTypeData?.max_extra_visitors_count ?? undefined,
            auto_renew: membershipTypeData?.auto_renew ?? false,
            allow_access_as_client_outside_restrictions:
                membershipTypeData?.allow_access_as_client_outside_restrictions ?? false,
            limit_to_roles: membershipTypeData?.limit_to_roles.map((role) => role.id) ?? undefined,
            custom_duration: membershipTypeData?.limit_duration && membershipTypeData?.duration === null,
            has_allowed_spaces_count: membershipTypeData?.has_allowed_spaces_count ?? false,
        };
    }, [membershipTypeData]);

    const form = useForm<MembershipTypeFormSchemaValues>({
        resolver: zodResolver(membershipTypeFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [membershipTypeData, isModalOpen, form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: MembershipTypeFormSchemaValues) => {
            const { custom_duration, ...rest } = values;

            mutate(
                rest,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [mutate, form, onError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <MembershipTypeForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedMembershipTemplate && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default MembershipTypeFormModal;
